/**
 * 엑셀식 컬럼 필터 팝오버 (검색 전용).
 *
 *   - 상단: 값 검색 입력
 *   - 중간: 모두 선택 + 컬럼의 고유값 체크박스 목록
 *     · 날짜 컬럼(type='date')은 **연 › 월 › 일 트리** (+ 로 펼침)
 *   - 하단 액션: 필터 해제 / 취소 / 적용
 *
 * ⭐**연쇄 필터** (2026-08-10) — 목록에는 **다른 컬럼 필터를 통과한 값만** 나온다.
 *   예전엔 원본 전체의 고유값을 보여 줘서, 거래처를 걸러 놓고 품목 필터를 열면
 *   그 거래처에 없는 품목까지 나왔다(골라도 0건). 엑셀과 같게 고쳤다.
 *   ⚠자기 컬럼 필터는 목록에 반영하지 않는다 — 그러면 뺀 값을 되살릴 수 없다.
 *
 * 정렬은 헤더 라벨 클릭으로 이미 처리 (오름→내림→해제). 본 팝오버에는 정렬 메뉴 없음.
 * 외부 클릭 또는 Escape → onClose
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import type { Column } from './types';
import type { ColStateApi } from './useColState';
import { uniqueValues, rowsForFilterOptions } from './applyColState';
import { buildDateTree, groupCheckState } from './dateTree';

interface Props<T> {
  column: Column<T>;
  /** 정렬·필터 적용 전 원본 행 (고유값 추출 기준) */
  baseRows: T[];
  /**
   * 표의 **모든** 컬럼 — 연쇄 필터에 필요하다(다른 컬럼 필터를 적용해 보려면 그 accessor 가 있어야 한다).
   * 안 넘기면 연쇄 없이 예전처럼 동작한다(하위호환).
   */
  columns?: Column<T>[];
  colState: ColStateApi;
  /** 팝오버의 위치를 잡을 트리거 버튼 (필터 아이콘) — fixed 좌표 계산에 사용 */
  anchorRef: RefObject<HTMLElement | null>;
  onClose: () => void;
}

const POPOVER_WIDTH = 288;     // = w-72 (Tailwind)
const VIEWPORT_PADDING = 8;

export function ColumnFilterPopover<T>({
  column,
  baseRows,
  columns,
  colState,
  anchorRef,
  onClose,
}: Props<T>) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [search, setSearch] = useState('');
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  /** 날짜 트리에서 펼쳐 둔 마디 ('2026' · '2026-8') */
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // 트리거 버튼 기준 위치 계산. 헤더 좌측 기준으로 시도하고, 화면 우측을 넘어가면 우측 클램프.
  // 표 첫 컬럼처럼 헤더 자체가 좌측에 가깝더라도 화면 좌측 패딩으로 자동 클램프 → 안전.
  useLayoutEffect(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    let left = rect.left;
    if (left + POPOVER_WIDTH > vw - VIEWPORT_PADDING) {
      left = vw - POPOVER_WIDTH - VIEWPORT_PADDING;
    }
    if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;
    const top = rect.bottom + 4;
    setPos({ top, left });
  }, [anchorRef]);

  /**
   * 목록에 쓸 행 — **다른 컬럼 필터를 통과한 행만** (엑셀식 연쇄 필터).
   * columns 를 안 받은 호출부는 예전처럼 원본 전체를 쓴다.
   */
  const optionRows = useMemo(
    () => (columns ? rowsForFilterOptions(baseRows, columns, colState, column.key) : baseRows),
    [baseRows, columns, colState, column.key],
  );

  const allUniques = useMemo(() => uniqueValues(optionRows, column), [optionRows, column]);
  const visibleUniques = useMemo(() => {
    if (!search.trim()) return allUniques;
    const term = search.trim().toLowerCase();
    return allUniques.filter(v => v.toLowerCase().includes(term));
  }, [allUniques, search]);

  /** 날짜 컬럼이면 연›월›일 트리 (검색 결과 기준으로 만든다 — 검색과 트리가 따로 놀지 않게) */
  const isDateCol = column.type === 'date';
  const tree = useMemo(
    () => (isDateCol ? buildDateTree(visibleUniques) : null),
    [isDateCol, visibleUniques],
  );

  // 검색 중에는 결과가 보이도록 연·월을 자동으로 펼친다 (닫힌 채로 0건처럼 보이면 안 된다)
  const searching = search.trim() !== '';
  const isOpen = (id: string): boolean => searching || expanded.has(id);
  const toggleOpen = (id: string): void =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // 적용 전 임시 선택. 키가 없으면 전체 선택 = 필터 없음.
  const initialSelected = useMemo(() => {
    const existing = colState.filters[column.key];
    return existing ? new Set(existing) : new Set(allUniques);
  }, [colState.filters, column.key, allUniques]);

  const [selected, setSelected] = useState<Set<string>>(initialSelected);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      // 필터 창만 닫고 그 아래 창(입출고 상세 등)은 닫지 않는다 (v2.231.0 · H-4)
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const allChecked = visibleUniques.every(v => selected.has(v));
  const someChecked = !allChecked && visibleUniques.some(v => selected.has(v));

  function toggleAll() {
    setSelected(prev => {
      const next = new Set(prev);
      if (allChecked) {
        // 검색 결과 모두 해제
        for (const v of visibleUniques) next.delete(v);
      } else {
        // 검색 결과 모두 선택
        for (const v of visibleUniques) next.add(v);
      }
      return next;
    });
  }

  function toggleOne(v: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });
  }

  /** 연·월 마디 토글 — 그 아래 raw 값 전체를 한 번에 켜고 끈다 */
  function toggleMany(values: string[], turnOn: boolean) {
    setSelected(prev => {
      const next = new Set(prev);
      for (const v of values) {
        if (turnOn) next.add(v);
        else next.delete(v);
      }
      return next;
    });
  }

  function applyFilter() {
    // 검색어 활성: 검색에 매칭되지 않은(=화면에 안 보이는) 항목은 사용자가 확인하지 못한
    // 상태이므로 [적용] 시 자동 제외. 엑셀의 검색→체크→적용 멘탈 모델과 일치.
    // 검색어가 없어도 **목록에 없던 값은 담지 않는다** — 연쇄 필터로 목록이 좁아졌을 때
    // 예전 선택이 남아 있으면, 나중에 다른 필터를 풀었을 때 왜 걸러지는지 알 수 없게 된다.
    const shownSet = new Set(search.trim() ? visibleUniques : allUniques);
    const next = new Set([...selected].filter((v) => shownSet.has(v)));

    // 보이는 값을 전부 골랐으면 = 이 컬럼엔 제한 없음 (개수 비교 대신 포함 여부로 — 더 안전)
    if (allUniques.every((v) => next.has(v))) {
      colState.clearFilter(column.key);
    } else {
      colState.setFilter(column.key, next);
    }
    onClose();
  }

  function clearFilterOnly() {
    colState.clearFilter(column.key);
    setSelected(new Set(allUniques));
    onClose();
  }

  // pos 가 계산되기 전엔 화면 밖에 두기 (깜빡임 방지 — invisible 로 측정만 하고 표시 안 함)
  const style = pos
    ? { top: pos.top, left: pos.left, width: POPOVER_WIDTH }
    : { top: -9999, left: -9999, width: POPOVER_WIDTH, visibility: 'hidden' as const };

  const node = (
    <div
      ref={ref}
      className="fixed z-50 bg-white border border-slate-300 rounded shadow-xl text-sm"
      style={style}
      onClick={(e) => e.stopPropagation()}
    >
      {/* 검색 + 체크박스 */}
      <div className="p-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            // Enter = 검색 결과만 적용 (마우스로 [적용] 버튼 누르는 것과 동일).
            // 단축 액션이라 일반 입력 중 IME 한글 조합이 안 끝났으면 무시.
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
              e.preventDefault();
              applyFilter();
            }
          }}
          placeholder="🔍 값 검색 (Enter 로 적용)"
          autoFocus
          className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-blue-500"
        />
        {/* 날짜 트리는 줄이 깊어져 더 높게 — 연·월을 펼쳤을 때 스크롤만 잔뜩 하지 않도록 */}
        <div className={`mt-2 overflow-y-auto ${tree ? 'max-h-80' : 'max-h-56'}`}>
          <label className="flex items-center gap-2 px-1 py-0.5 hover:bg-slate-50 cursor-pointer">
            <input
              type="checkbox"
              checked={allChecked}
              ref={el => { if (el) el.indeterminate = someChecked; }}
              onChange={toggleAll}
            />
            <strong className="text-xs">
              {search.trim() ? '(검색 결과 모두 선택)' : '(모두 선택)'}
            </strong>
          </label>
          {visibleUniques.length === 0 && (
            <div className="text-xs text-slate-500 py-2 text-center">일치하는 값 없음</div>
          )}

          {/* 날짜 컬럼 — 연 › 월 › 일 트리 (엑셀과 같은 모양) */}
          {tree && tree.years.map((y) => {
            const yState = groupCheckState(y.values, selected);
            const yId = `y${y.year}`;
            return (
              <div key={yId}>
                <div className="flex items-center gap-1 px-1 py-0.5 hover:bg-slate-50">
                  <button
                    type="button"
                    onClick={() => toggleOpen(yId)}
                    className="w-4 text-[11px] text-slate-500 hover:text-blue-700 leading-none"
                    aria-label={`${y.label} ${isOpen(yId) ? '접기' : '펼치기'}`}
                    aria-expanded={isOpen(yId)}
                  >
                    {isOpen(yId) ? '−' : '+'}
                  </button>
                  <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                    <input
                      type="checkbox"
                      checked={yState.all}
                      ref={el => { if (el) el.indeterminate = yState.some; }}
                      onChange={() => toggleMany(y.values, !yState.all)}
                    />
                    <span className="text-xs font-semibold">{y.label}</span>
                    <span className="text-[11px] text-slate-500">({y.values.length})</span>
                  </label>
                </div>

                {isOpen(yId) && y.months.map((mo) => {
                  const mState = groupCheckState(mo.values, selected);
                  const mId = `${yId}-m${mo.month}`;
                  return (
                    <div key={mId}>
                      <div className="flex items-center gap-1 pl-4 pr-1 py-0.5 hover:bg-slate-50">
                        <button
                          type="button"
                          onClick={() => toggleOpen(mId)}
                          className="w-4 text-[11px] text-slate-500 hover:text-blue-700 leading-none"
                          aria-label={`${y.label} ${mo.label} ${isOpen(mId) ? '접기' : '펼치기'}`}
                          aria-expanded={isOpen(mId)}
                        >
                          {isOpen(mId) ? '−' : '+'}
                        </button>
                        <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                          <input
                            type="checkbox"
                            checked={mState.all}
                            ref={el => { if (el) el.indeterminate = mState.some; }}
                            onChange={() => toggleMany(mo.values, !mState.all)}
                          />
                          <span className="text-xs">{mo.label}</span>
                          <span className="text-[11px] text-slate-500">({mo.values.length})</span>
                        </label>
                      </div>

                      {isOpen(mId) && mo.days.map((d) => {
                        const dState = groupCheckState(d.values, selected);
                        return (
                          <label
                            key={`${mId}-d${d.day}`}
                            className="flex items-center gap-2 pl-12 pr-1 py-0.5 hover:bg-slate-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={dState.all}
                              ref={el => { if (el) el.indeterminate = dState.some; }}
                              onChange={() => toggleMany(d.values, !dState.all)}
                            />
                            <span className="text-xs">{d.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* 날짜로 못 읽은 값 — 버리지 않고 아래에 평범한 체크박스로 */}
          {tree && tree.others.map((v) => (
            <label
              key={`o-${v}`}
              className={`flex items-center gap-2 px-1 py-0.5 hover:bg-slate-50 cursor-pointer ${v === '' ? 'text-slate-400 italic' : ''}`}
            >
              <input type="checkbox" checked={selected.has(v)} onChange={() => toggleOne(v)} />
              <span className="text-xs truncate" title={v === '' ? '(빈 값)' : v}>
                {v === '' ? '(빈 값)' : v}
              </span>
            </label>
          ))}

          {/* 그 밖의 컬럼 — 지금까지와 같은 평평한 목록 */}
          {!tree && visibleUniques.map(v => {
            const isEmpty = v === '';
            return (
              <label
                key={v}
                className={`flex items-center gap-2 px-1 py-0.5 hover:bg-slate-50 cursor-pointer ${isEmpty ? 'text-slate-400 italic' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(v)}
                  onChange={() => toggleOne(v)}
                />
                <span className="text-xs truncate" title={isEmpty ? '(빈 값)' : v}>
                  {isEmpty ? '(빈 값)' : v}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {/* 검색어 활성 시 [적용] 동작 안내 */}
      {search.trim() && (
        <div className="px-2 pb-1 text-[11px] text-slate-500 leading-snug">
          ℹ 검색 결과 중 체크된 항목만 표시됩니다. (검색에 안 잡힌 항목은 자동 제외)
        </div>
      )}
      {/* 액션 */}
      <div className="flex items-center justify-between border-t border-slate-200 px-2 py-2">
        <button
          type="button"
          onClick={clearFilterOnly}
          className="text-xs text-slate-500 hover:text-blue-600 hover:underline"
          title="이 컬럼의 필터 해제"
        >
          필터 해제
        </button>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50"
          >
            취소
          </button>
          <button
            type="button"
            onClick={applyFilter}
            className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
            title={search.trim() ? '검색 결과 중 체크된 항목만 표시' : '체크된 항목만 표시'}
          >
            {search.trim() ? '검색 결과만 적용' : '적용'}
          </button>
        </div>
      </div>
    </div>
  );

  // body 에 portal — 표 컨테이너의 overflow 제약을 벗어나 화면 어디든 표시 가능
  return createPortal(node, document.body);
}
